import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import type { ApiError } from "../../lib/api.ts";
import type { AssuranceFindingDetail } from "./use-assurance.ts";
import {
  severityLabel,
  severityStatus,
  severityTitle,
  statusLabel,
  statusStatus,
  engineLabelOrNull,
  actionLabel,
  actionStatus,
  actorTypeLabel,
  findingHref,
} from "./vocab.ts";

/**
 * The detail shown in the right-side drawer for one assurance finding (task 7.4; requirement 13.4
 * "per-finding decision history"). It is sourced from `GET /assurance/findings/{fingerprint}` →
 * `AssuranceFindingDetail` (the finding plus its decisions, newest first), fetched lazily when a row
 * is selected.
 *
 * It renders its own loading / error / ready state inside the drawer, so a slow or failing detail
 * read never blanks the whole screen — the list behind stays usable. The body is a header (severity
 * + status by icon+text, never colour alone — design 12.4), a facts grid (fingerprint, engine, CWE,
 * rule, file ref, agent, first/last seen) and the DECISION HISTORY timeline (each decision's action
 * as an icon+text badge, who took it and when, newest first). Every nullable field (engine, cwe,
 * rule_id, file_ref, agent_sub, actor_type) is shown honestly as "—"/"Unknown" rather than fabricated.
 *
 * Pure presentation: it takes the selected fingerprint, the detail query result and a close callback;
 * it does no fetching of its own (the screen owns the query) and only navigates on the Events drill.
 */

export interface FindingDetailProps {
  /** The fingerprint of the selected finding (what the detail was fetched for). */
  readonly fingerprint: string;
  /** The lazily-fetched finding detail: data, loading and error states. */
  readonly detail: {
    readonly data: AssuranceFindingDetail | undefined;
    readonly isError: boolean;
    readonly error: ApiError | null;
  };
  readonly onClose: () => void;
  /** The id the drawer dialog is labelled by (the header heading). */
  readonly titleId: string;
}

export function FindingDetail(props: FindingDetailProps): ReactNode {
  const navigate = useNavigate();
  const { detail } = props;

  return (
    <>
      <header className="asr-drawer__head">
        <div className="asr-drawer__head-main">
          <h2 id={props.titleId} className="asr-drawer__heading">
            Finding
          </h2>
          <code className="asr-mono asr-drawer__fingerprint">{props.fingerprint}</code>
        </div>
        <button
          type="button"
          className="asr-drawer__close"
          onClick={props.onClose}
          aria-label="Close finding details"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="asr-drawer__body">
        {detail.data !== undefined ? (
          <ReadyDetail
            data={detail.data}
            onEvents={() => navigate(findingHref(detail.data!.finding))}
          />
        ) : detail.isError ? (
          <div className="asr-drawer__state" data-kind="error" role="alert">
            <strong>Decision history unavailable</strong>
            <span>{detailErrorMessage(detail.error)}</span>
          </div>
        ) : (
          <div className="asr-drawer__state" role="status" aria-live="polite">
            <strong>Loading finding…</strong>
            <span>Fetching the decision history for this finding.</span>
          </div>
        )}
      </div>
    </>
  );
}

/** The finding facts + decision-history timeline, once the detail has loaded. */
function ReadyDetail(props: {
  data: AssuranceFindingDetail;
  onEvents: () => void;
}): ReactNode {
  const { finding, decisions } = props.data;

  return (
    <>
      <section className="asr-drawer__section" aria-label="Finding">
        <div className="asr-drawer__badges">
          <StatusBadge
            level={severityStatus(finding.severity)}
            label={severityLabel(finding.severity)}
            title={severityTitle(finding.severity)}
            size="sm"
          />
          <StatusBadge
            level={statusStatus(finding.status)}
            label={statusLabel(finding.status)}
            size="sm"
          />
        </div>

        <dl className="asr-kv">
          <Fact k="Engine" v={engineLabelOrNull(finding.engine)} />
          <Fact k="Rule" v={mono(finding.rule_id)} />
          <Fact k="CWE" v={mono(finding.cwe)} />
          <Fact k="File reference" v={mono(finding.file_ref)} />
          <Fact
            k="Agent"
            v={
              finding.agent_sub === null || finding.agent_sub === undefined ? (
                <span className="asr-faint">No agent</span>
              ) : (
                <code className="asr-mono">{finding.agent_sub}</code>
              )
            }
          />
          <Fact
            k="First seen"
            v={
              <time dateTime={isoAttr(finding.first_seen)}>
                {formatDateTime(finding.first_seen)}
              </time>
            }
          />
          <Fact
            k="Last seen"
            v={
              <time dateTime={isoAttr(finding.last_seen)}>
                {formatDateTime(finding.last_seen)}
              </time>
            }
          />
        </dl>

        <button type="button" className="asr-link" onClick={props.onEvents}>
          Open the events for this finding
        </button>
      </section>

      <section className="asr-drawer__section" aria-label="Decision history">
        <h3 className="asr-drawer__section-title">Decision history</h3>
        {decisions.length === 0 ? (
          <p className="asr-faint asr-drawer__empty">
            No decisions have been recorded for this finding yet.
          </p>
        ) : (
          <ol className="asr-timeline">
            {decisions.map((d, i) => (
              <li className="asr-timeline__item" key={`${d.ts}-${i}`}>
                <div className="asr-timeline__badge">
                  <StatusBadge
                    level={actionStatus(d.action)}
                    label={actionLabel(d.action)}
                    size="sm"
                  />
                </div>
                <div className="asr-timeline__meta">
                  <span className="asr-faint">{actorTypeLabel(d.actor_type)}</span>
                  {d.agent_sub !== null && d.agent_sub !== undefined && (
                    <code className="asr-mono">{d.agent_sub}</code>
                  )}
                  <time className="asr-num" dateTime={isoAttr(d.ts)}>
                    {formatDateTime(d.ts)}
                  </time>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
}

/** One row of the facts grid. */
function Fact(props: { k: string; v: ReactNode }): ReactNode {
  return (
    <div className="asr-kv__row">
      <dt className="asr-kv__k">{props.k}</dt>
      <dd className="asr-kv__v">{props.v}</dd>
    </div>
  );
}

/** Render a nullable id-ish string as mono, or an honest em dash when absent. */
function mono(value: string | null | undefined): ReactNode {
  if (value === null || value === undefined || value.length === 0) {
    return <span className="asr-faint">—</span>;
  }
  return <code className="asr-mono">{value}</code>;
}

/** A readable message for a detail-read failure (never the raw waking error, which the shell owns). */
function detailErrorMessage(error: ApiError | null): string {
  if (error === null) return "The finding detail could not load.";
  switch (error.kind) {
    case "not_found":
      return "This finding is no longer available. It may have aged out of the window.";
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see this finding. The server enforces access.";
    case "network":
      return "The request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}
